import { ChangeDetectorRef, Component, effect, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppMenuitem } from '../menuitem/app.menuitem';
import { Role } from '@/core/models/auth.interface';
import { GenericProfileService } from '@/shared/services/profile.service';
import { ExtendedMenuItem, BoxSection } from './menu.types';
import { GlobalProgressSpinnerService } from '@/shared/services/global-progress-spinner.service';
import { AuthService } from '@/core/services/auth.service';
import { finalize, Observable } from 'rxjs';

import { getAdminMenuItems } from './menu-configs/admin-menu.config';
import { getWealthManagerMenuItems } from './menu-configs/wealth-manager-menu.config';
import { getRelationshipManagerMenuItems } from './menu-configs/relationship-manager-menu.config';
import {
    getClientMenuItems,
    mapClientDealsSummaryToOpportunityCounts,
} from './menu-configs/client-menu.config';
import { DialogComponent } from '@/shared/ui-components/dialog/dialog.component';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '@/pages/notifications/notifications.service';
import { NotificationsSummary } from '@/pages/notifications/data-access/notifications.model';
import { DealsService } from '@/pages/deals/deals.service';
import { UserProfileService } from '@/pages/user-profile/user-profile.service';
import { AiChatDrawerService } from '@/pages/ai-chat/components/ai-chat-drawer/ai-chat-drawer.service';
import { AdminOpenTasksSummaryDto, findOpenTasksSectionByName, findOpenTasksSubsectionCount } from '@/pages/dashboards/admin-dashboard/data-access/admin-dashboard.model';
import { AdminDashboardService } from '@/pages/dashboards/admin-dashboard/admin-dashboard.service';
import { WealthManagerDashboardService } from '@/pages/dashboards/wealth-manager-dashboard/wealth-manager-dashboard.service';
import { RelationshipManagerDashboardService } from '@/pages/dashboards/relationship-manager-dashboard/relationship-manager-dashaboard.service';
import { DealsApiService } from '@/pages/deals/data-access/deals-api.service';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule, AngularSvgIconModule, DialogComponent, ButtonComponent, TranslateModule],
    templateUrl: './app.menu.html'
})
export class AppMenu implements OnInit {
    public model: MenuItem[] = [];
    public showLogoutConfirmDialog = false;
    private readonly _router = inject(Router);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _globalProgressSpinnerService = inject(GlobalProgressSpinnerService);
    private readonly _authService = inject(AuthService);
    private readonly _dealsService = inject(DealsService);
    private readonly _notificationsService = inject(NotificationsService);
    private readonly _genericProfileService = inject(GenericProfileService);
    private readonly _userProfileService = inject(UserProfileService);
    private readonly _translateService = inject(TranslateService);
    private readonly _aiChatDrawerService = inject(AiChatDrawerService);
    private readonly _adminDashboardService = inject(AdminDashboardService);
    private readonly _wmDashboardService = inject(WealthManagerDashboardService);
    private readonly _rmDashboardService = inject(RelationshipManagerDashboardService);
    private readonly _dealsApiService = inject(DealsApiService);
    private readonly _notificationsHasError = signal<boolean>(false);
    private readonly _clientDealsSummaryHasError = signal<boolean>(false);
    private readonly _isClientUser = signal<boolean>(false);
    private readonly _isAdminUser = signal<boolean>(false);
    private readonly _isWmUser = signal<boolean>(false);
    private readonly _isRmUser = signal<boolean>(false);
    private readonly _openTasksSummary = signal<AdminOpenTasksSummaryDto | null>(null);
    // Rebuild client opportunities submenu when summary changes (keeps all 8 tabs in sidenav).
    private readonly _clientDealsMenuEffect = effect(() => {
        const summary = this._dealsService.clientDealsSummary();

        if (!this._isClientUser() || !summary) {
            return;
        }

        this.model = [
            {
                label: '',
                items: getClientMenuItems(
                    () => this.onLogoutClick(),
                    mapClientDealsSummaryToOpportunityCounts(summary),
                    undefined,
                    (key) => this._translateService.instant(key),
                    () => this.onAiChatClick()
                ),
            },
        ];
        const openTasksSummary = this._openTasksSummary();
        if (openTasksSummary) {
            this.setOpenTasksBadges(openTasksSummary);
        }
        this._changeDetectorRef.markForCheck();
    });
    // it is Angular signal effect, it runs automatically
    private readonly _notificationsBadgeEffect = effect(() => {
        const isLoading = this._notificationsService.notificationsSummaryLoading();
        const hasError = this._notificationsHasError();
        const unread = this._notificationsService.unreadCount();

        if (isLoading) {
            this.setLabelByRoute('/notifications', 'Notifications (Loading...)');
            this.setBadgeByRoute('/notifications', 0);
            this._changeDetectorRef.detectChanges();
            return;
        }

        if (hasError) {
            this.setLabelByRoute('/notifications', 'Notifications (Error)');
            this.setBadgeByRoute('/notifications', 0);
            this._changeDetectorRef.detectChanges();
            return;
        }

        this.setLabelByRoute('/notifications', 'Notifications');
        this.setBadgeByRoute('/notifications', unread);
        this._changeDetectorRef.detectChanges();
    });

    /**
     * Check if menu item is a box-submenu type
     */
    public isBoxSubmenu(item: MenuItem): boolean {
        const extendedItem = item as ExtendedMenuItem;
        return extendedItem.menuType === 'box-submenu' && !!extendedItem.boxSections;
    }

    /**
     * Get box sections from menu item
     */
    public getBoxSections(item: MenuItem): BoxSection[] | undefined {
        const extendedItem = item as ExtendedMenuItem;
        return extendedItem.boxSections;
    }

    /**
     * Check if any route in a box section is active
     */
    public isBoxSectionActive(boxSection: BoxSection): boolean {
        if (!boxSection.items) return false;
        return boxSection.items.some(item => {
            if (item.routerLink) {
                return this._router.isActive(item.routerLink[0], {
                    paths: 'exact',
                    queryParams: 'subset',
                    matrixParams: 'ignored',
                    fragment: 'ignored'
                });
            }
            return false;
        });
    }
    /** Called when user clicks Logout in sidebar menu – opens confirmation dialog. */
    public onLogoutClick(): void {
        this.showLogoutConfirmDialog = true;
    }

    /** Called when user clicks "Ask BettaAlpha" in the sidebar menu – opens the chat drawer. */
    public onAiChatClick(): void {
        this._aiChatDrawerService.open();
    }

    /** Called when user confirms logout (Yes) – close dialog and perform logout. */
    public confirmLogout(): void {
        this.showLogoutConfirmDialog = false;
        this._globalProgressSpinnerService.showLoader();
        this._authService
        .logout()
        .pipe(finalize(() => this._globalProgressSpinnerService.hideLoader()))
        .subscribe({
            next: () => {
                    this._userProfileService.clearProfileState();
                    this._router.navigate(['/auth/login']);
                }
            });
    }

    public ngOnInit(): void {
        const role = this._genericProfileService.genericProfile()!.role;
        this._isClientUser.set(role === Role.CLIENT);
        let home: MenuItem = {};
        if (
            role === Role.SUPER_ADMIN ||
            role === Role.ADMIN_DEAL_USER_PREPARER ||
            role === Role.ADMIN_DEAL_USER_APPROVER ||
            role === Role.ADMIN_CLIENT_USERS
        ) {
            this._isAdminUser.set(true);
            home = {
                items: getAdminMenuItems(() => this.onLogoutClick(), role, () => this.onAiChatClick())
            };
            this.model = [home];
            this.loadMenuBadges();
        } else if (role === Role.WEALTH_MANAGER_SUPER_USER || role === Role.WEALTH_MANAGER_DEALS || role === Role.WEALTH_MANAGER_USERS) {
            this._isWmUser.set(true);
            home = getWealthManagerMenuItems(() => this.onLogoutClick(), role, () => this.onAiChatClick());
            this.model = [home];
            this.loadMenuBadges();
        } else if (role === Role.RELATIONSHIP_MANAGER) {
            this._isRmUser.set(true);
            home = getRelationshipManagerMenuItems(() => this.onLogoutClick(), () => this.onAiChatClick());
            this.model = [home];
            this.loadMenuBadges();
        } else if (role === Role.CLIENT) {
            this.model = [
                {
                    label: '',
                    items: getClientMenuItems(
                        () => this.onLogoutClick(),
                        undefined,
                        { loading: true },
                        (key) => this._translateService.instant(key),
                        () => this.onAiChatClick()
                    ),
                },
            ];
            this.loadMenuBadges();
        }
    }

    private loadMenuBadges(): void {
        this._notificationsHasError.set(false);
        this._clientDealsSummaryHasError.set(false);

        const openTasksRequest$ = this.resolveOpenTasksSummaryRequest$();
        if (openTasksRequest$) {
            openTasksRequest$.subscribe({
                next: (summary: AdminOpenTasksSummaryDto) => {
                    this._openTasksSummary.set(summary);
                    this.setOpenTasksBadges(summary);
                },
                error: (err) => {
                    console.error('Failed to load open tasks summary for menu badges.', err);
                },
            });
        }

        // Client only: refresh opportunities summary (signal-backed)
        if (this._isClientUser()) {
            this._dealsService
                .fetchClientDealsSummary$()
                .subscribe({
                    next: (summary) => {
                        this._clientDealsSummaryHasError.set(false);
                        this._dealsService.clientDealsSummary.set(summary);
                    },
                    error: () => {
                        this._clientDealsSummaryHasError.set(true);
                        this._dealsService.clientDealsSummary.set(null);

                        this.model = [
                            {
                                label: '',
                                items: getClientMenuItems(
                                    () => this.onLogoutClick(),
                                    undefined,
                                    { error: true },
                                    (key) => this._translateService.instant(key),
                                    () => this.onAiChatClick()
                                ),
                            },
                        ];
                        const openTasksSummary = this._openTasksSummary();
                        if (openTasksSummary) {
                            this.setOpenTasksBadges(openTasksSummary);
                        }
                        this._changeDetectorRef.markForCheck();
                    },
                });
        }

        this._notificationsService
            .getNotificationsSummary$()
            .subscribe({
                next: (_summary: NotificationsSummary) => {
                    this._notificationsHasError.set(false);
                },
                error: (err) => {
                    console.error('Failed to load notifications summary for menu badges.', err);
                    this._notificationsHasError.set(true);
                }
            });

    }

    private resolveOpenTasksSummaryRequest$(): Observable<AdminOpenTasksSummaryDto> | null {
        if (this._isAdminUser() && this._genericProfileService.genericProfile()?.role !== Role.ADMIN_CLIENT_USERS) {
            return this._adminDashboardService.getOpenTasksSummary();
        }
        if (this._isWmUser()) {
            return this._wmDashboardService.getOpenTasksSummary();
        }
        if (this._isRmUser()) {
            return this._rmDashboardService.getOpenTasksSummary();
        }
        if (this._isClientUser()) {
            return this._dealsApiService.getClientOpenTasksSummary();
        }
        return null;
    }

    private getMenuItemRouterLink(item: MenuItem): string | undefined {
        if (!item.routerLink) {
            return undefined;
        }
        if (Array.isArray(item.routerLink)) {
            return item.routerLink.map((segment) => String(segment)).join('/');
        }
        return String(item.routerLink);
    }

    /** Tasks submenu parent (not the "All" child — both can relate to `/tasks`). */
    private isTasksMenuParent(item: MenuItem): boolean {
        return !!item.items?.length && String(item.label ?? '').toLowerCase().startsWith('tasks');
    }

    private isTasksBoxSubmenu(item: MenuItem): boolean {
        const extendedItem = item as ExtendedMenuItem;
        return (
            extendedItem.menuType === 'box-submenu' &&
            String(item.label ?? '').toLowerCase().startsWith('tasks') &&
            !!extendedItem.boxSections?.length
        );
    }

    private setOpenTasksBadges(summary: AdminOpenTasksSummaryDto): void {
        const total = summary.total ?? summary.sections.reduce((sum, section) => sum + section.count, 0);
        const formatBadge = (count: number): string | undefined => (count > 0 ? String(count) : undefined);

        const transform = (item: MenuItem): MenuItem => {
            const extendedItem = item as ExtendedMenuItem;
            const nextItem: MenuItem = { ...item };

            if (this.isTasksBoxSubmenu(item)) {
                nextItem.badge = formatBadge(total);
                (nextItem as ExtendedMenuItem).boxSections = extendedItem.boxSections!.map((section) => {
                    const apiSection = findOpenTasksSectionByName(summary, section.title);
                    const sectionCount = apiSection?.count ?? 0;
                    return {
                        ...section,
                        badge: formatBadge(sectionCount),
                        items:
                            section.items?.map((boxItem) => {
                                const subsectionCount = findOpenTasksSubsectionCount(
                                    apiSection,
                                    String(boxItem.label ?? ''),
                                );
                                return {
                                    ...boxItem,
                                    badge: formatBadge(subsectionCount),
                                };
                            }) ?? [],
                    };
                });
            }

            if (item.items?.length) {
                nextItem.items = item.items.map(transform);
            }
            if (extendedItem.boxSections?.length && !this.isTasksBoxSubmenu(item)) {
                (nextItem as ExtendedMenuItem).boxSections = extendedItem.boxSections.map((section) => ({
                    ...section,
                    items: section.items?.map(transform) ?? [],
                }));
            }
            return nextItem;
        };

        this.model = this.model.map((root) => ({
            ...root,
            items: root.items?.map(transform) ?? root.items,
        }));
        this._changeDetectorRef.markForCheck();
    }

    private setBadgeByRoute(route: string, count: number | string): void {
        const routeSuffix = route.startsWith('/') ? route : `/${route}`;
        const transform = (item: MenuItem): MenuItem => {
            const link = this.getMenuItemRouterLink(item);
            const nextItem: MenuItem = { ...item };
            const isTasksParent = routeSuffix === '/tasks' && this.isTasksMenuParent(item);
            const routeMatches =
                typeof link === 'string' &&
                (link === route || (route !== '/client/deals' && routeSuffix !== '/tasks' && link.endsWith(routeSuffix)));
            if (isTasksParent || routeMatches) {
                nextItem.badge = typeof count === 'string' ? count : (count > 0 ? String(count) : undefined);
            }
            if (item.items?.length) {
                nextItem.items = item.items.map(transform);
            }
            const extendedItem = item as ExtendedMenuItem;
            if (extendedItem.boxSections?.length) {
                (nextItem as ExtendedMenuItem).boxSections = extendedItem.boxSections.map((section) => ({
                    ...section,
                    items: section.items?.map(transform) ?? []
                }));
            }
            return nextItem;
        };

        this.model = this.model.map((root) => ({
            ...root,
            items: root.items?.map(transform) ?? root.items
        }));
        this._changeDetectorRef.markForCheck();
    }

    private setLabelByRoute(route: string, label: string): void {
        const routeSuffix = route.startsWith('/') ? route : `/${route}`;
        const transform = (item: MenuItem): MenuItem => {
            const link = this.getMenuItemRouterLink(item);
            const nextItem: MenuItem = { ...item };
            const isTasksParent = routeSuffix === '/tasks' && this.isTasksMenuParent(item);
            const routeMatches =
                typeof link === 'string' &&
                (link === route || (routeSuffix !== '/tasks' && link.endsWith(routeSuffix)));
            // `/tasks` updates only the Tasks parent label — never the "All" child (same route).
            if ((routeSuffix === '/tasks' && isTasksParent) || (routeSuffix !== '/tasks' && routeMatches)) {
                nextItem.label = label;
            }
            if (item.items?.length) {
                nextItem.items = item.items.map(transform);
            }
            const extendedItem = item as ExtendedMenuItem;
            if (extendedItem.boxSections?.length) {
                (nextItem as ExtendedMenuItem).boxSections = extendedItem.boxSections.map((section) => ({
                    ...section,
                    items: section.items?.map(transform) ?? []
                }));
            }
            return nextItem;
        };

        this.model = this.model.map((root) => ({
            ...root,
            items: root.items?.map(transform) ?? root.items
        }));
        this._changeDetectorRef.markForCheck();
    }
}
